'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { fetchResultadosPublicos, fetchIndicadoresVigentes, type ResultadoPublicoApi, type IndicadorVigenteApi } from '@/services/indicadores';

export default function IndicatorDetailPage({ params }: { params: { id: string } }) {
  const [loading, setLoading] = useState(true);
  const [indicatorMeta, setIndicatorMeta] = useState<IndicadorVigenteApi | null>(null);
  const [historicalData, setHistoricalData] = useState<ResultadoPublicoApi[]>([]);

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        const [metaRes, histRes] = await Promise.all([
          fetchIndicadoresVigentes({ codigo_interno: params.id }),
          fetchResultadosPublicos({ codigo_interno: params.id })
        ]);
        if (active) {
          if (metaRes.length > 0) {
            setIndicatorMeta(metaRes[0]);
          }
          if (histRes.length > 0) {
            setHistoricalData(histRes.sort((a, b) => new Date(a.periodo_referencia).getTime() - new Date(b.periodo_referencia).getTime()));
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch indicator detail', err);
        if (active) setLoading(false);
      }
    };
    loadData();
    return () => { active = false; };
  }, [params.id]);

  const displayTitle = indicatorMeta?.indicador || 'Total de Fuerza Laboral';
  const displayCategory = indicatorMeta?.categoria || 'Actividad Económica';
  const displayPeriod = indicatorMeta?.version || '2022';
  const displayUnit = indicatorMeta?.unidad_medida || 'Personas';
  const displaySymbol = indicatorMeta?.unidad_simbolo || '';
  const displayDescription = indicatorMeta?.formula_texto || 'Mide la cantidad total de personas económicamente activas en el partido de Luján.';
  const displayFormula = indicatorMeta?.formula_expression || 'Σ(Población económicamente activa)';
  const displayPeriodicidad = indicatorMeta?.periodicidad || 'Censal (10 años)';

  // Calculate chart data from real database computed results!
  const hasHistData = historicalData.length > 0;
  const chartData = hasHistData
    ? historicalData.map((res, i) => {
        const year = res.periodo_referencia ? new Date(res.periodo_referencia).getFullYear() : 2022;
        const value = typeof res.valor_resultado === 'number'
          ? res.valor_resultado
          : parseFloat(String(res.valor_resultado).replace(/[^\d.-]/g, '')) || 0;
        const label = typeof res.valor_resultado === 'number'
          ? res.valor_resultado.toLocaleString('es-AR')
          : String(res.valor_resultado);
        const opacities = ['bg-[#89B6FF]', 'bg-[#5B95FF]', 'bg-[#2E74FF]', 'bg-[#0053D6]'];
        const opacity = opacities[i % opacities.length];

        return { year, value, label, opacity };
      })
    : [
        { year: 2010, value: 34200, label: '34.200', opacity: 'bg-[#89B6FF]' },
        { year: 2015, value: 39900, label: '39.900', opacity: 'bg-[#5B95FF]' },
        { year: 2020, value: 45220, label: '45.220', opacity: 'bg-[#2E74FF]' },
        { year: 2022, value: 53200, label: '53.200', opacity: 'bg-[#0053D6]' },
      ];

  const maxValue = Math.max(...chartData.map(d => d.value), 1);

  const displayLatestValue = hasHistData && historicalData.length > 0
    ? typeof historicalData[historicalData.length - 1].valor_resultado === 'number'
      ? historicalData[historicalData.length - 1].valor_resultado.toLocaleString('es-AR')
      : String(historicalData[historicalData.length - 1].valor_resultado)
    : '53.481';

  const displayLatestPeriod = hasHistData && historicalData.length > 0
    ? historicalData[historicalData.length - 1].periodo_referencia
      ? new Date(historicalData[historicalData.length - 1].periodo_referencia).getFullYear().toString()
      : '2022'
    : '2022';

  return (
    <>
      {/* Breadcrumbs */}
      <div className="max-w-6xl mx-auto w-full px-8 py-4 text-sm text-gray-400 font-medium">
        <Link href="/" className="hover:text-[#0053D6] transition-colors">Inicio</Link>
        <span className="mx-2">/</span>
        <Link href="/indicadores" className="hover:text-[#0053D6] transition-colors">Indicadores</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-600">{displayTitle}</span>
      </div>

      <main className="max-w-6xl mx-auto w-full px-8 pb-16 flex-1 flex flex-col gap-6">
        
        {/* Header Card del Indicador */}
        <div className="bg-[#F0F5FA] rounded-2xl p-8 border border-blue-50">
          <div className="flex items-center gap-4 mb-3">
            <h2 className="text-3xl md:text-4xl font-[family-name:var(--font-righteous)] text-[#003876] tracking-wide">
              {displayTitle}
            </h2>
            <span className="bg-[#D1E1FF] text-[#0053D6] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              Publicado
            </span>
          </div>
          <div className="text-sm text-[#0053D6]/80 font-medium flex flex-wrap gap-2">
            <span>Categoría: {displayCategory}</span>
            <span>·</span>
            <span>Período vigente: {displayLatestPeriod}</span>
            <span>·</span>
            <span>Fuente: Ficha Técnica</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Valor Vigente (Izquierda) */}
          <div className="bg-[#003876] text-white rounded-2xl p-8 flex flex-col justify-center shadow-lg lg:col-span-1">
            <h3 className="text-sm font-medium text-blue-200 mb-2">Valor Vigente</h3>
            <div className="text-5xl md:text-6xl font-[family-name:var(--font-righteous)] mb-2 break-words">
              {displayLatestValue}
            </div>
            <div className="text-blue-100/70 text-sm">
              {displayUnit} · Período {displayLatestPeriod}
            </div>
          </div>

          {/* Ficha Metodológica (Derecha) */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 lg:col-span-2">
            <h3 className="text-xl font-bold text-gray-800 mb-6">Ficha Metodológica</h3>
            
            <div className="space-y-4">
              <div className="border-b border-gray-100 pb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Descripción / Metodología</p>
                <p className="text-sm text-gray-700">{displayDescription}</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Fórmula de Cálculo</p>
                <p className="text-sm text-gray-700 font-mono text-xs bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100/80 inline-block">{displayFormula}</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Unidad de Medida</p>
                <p className="text-sm text-gray-700">{displayUnit} {displaySymbol ? `(${displaySymbol})` : ''}</p>
              </div>
              <div className="border-b border-gray-100 pb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Periodicidad</p>
                <p className="text-sm text-gray-700">{displayPeriodicidad}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Serie Histórica (Gráfico de Barras con Tailwind) */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mt-2">
          <h3 className="text-xl font-bold text-gray-800 mb-10">Serie Histórica</h3>
          
          {/* Chart Container */}
          <div className="h-64 w-full flex items-end justify-center sm:justify-start gap-8 sm:gap-16 px-4 mb-4">
            {chartData.map((data, idx) => (
              <div key={idx} className="flex flex-col items-center group w-16 sm:w-24">
                <span className="text-xs sm:text-sm font-bold text-[#003876] mb-2">{data.label}</span>
                <div 
                  className={`w-full rounded-t-lg transition-all duration-500 ease-in-out ${data.opacity} hover:opacity-80`}
                  style={{ height: `${(data.value / maxValue) * 100}%` }}
                ></div>
                <div className="w-full border-t-2 border-gray-200 pt-3 mt-0 text-center">
                  <span className="text-sm font-bold text-gray-400">{data.year}</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 border-t border-gray-100 pt-4">
            <p className="text-xs text-gray-400">* Valores de corridas e importaciones aprobadas oficialmente en Luján.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mt-4 border-b border-gray-200 pb-12">
          <a 
            href={`/api/externo/resultados-publicos?codigo_interno=${params.id}&format=csv`}
            className="bg-[#0052A3] hover:bg-[#003876] text-white font-bold py-3 px-8 rounded-lg shadow transition-colors inline-block text-center"
          >
            Descargar CSV
          </a>
          <button 
            onClick={() => window.print()}
            className="bg-white hover:bg-gray-50 text-[#0052A3] font-bold py-3 px-8 rounded-lg shadow-sm border border-gray-200 transition-colors"
          >
            Descargar PDF
          </button>
        </div>

      </main>
    </>
  );
}
